import React, { useState, useEffect, useRef, createContext} from 'react';

//icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Box, Button, ButtonBase, CssBaseline, Grid, InputAdornment, OutlinedInput, Stack, Toolbar, Typography } from '@mui/material';
// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { shouldForwardProp } from '@mui/system';

//project imports
import ExtranetLogo from '../../components/nav/ExtranetLogo';

import Dashboard from './components/DashboardRoot';
import FilersRoot from './components/FilersRoot';
import './view-archer-styles.css';
import AlertInfoDialog from '../../components/elements/AlertInfoDialog';
import AlertInfo from '../../dataObjects/AlertInfo';

const DisplayStatus = {
    Dashboard: "active",
    DashboardButton: 'hidden',
    Filers: 'hidden',
    FilerSummary: 'hidden',
    FilersButton: 'active',
    Referral: 'hidden',
    ReferralButton: 'active',
    Audit205: 'hidden',
    Audit205Button: 'active'
}

const defaultAuditorId = 0;

//change auditor id across components
export interface AuditorCommunication {
    currentAuditorId: number;
    currentLetterCpfId: number;
    currentLetterId: number;
    setCurrentAuditorId: (newId: number) => void;
    setCurrentLetterCpfId: (newId: number) => void;
    setCurrentLetterId: (newId: number) => void;
}

export const AuditorCommunicationContext = createContext<AuditorCommunication>({
    currentAuditorId: -1,
    currentLetterCpfId: 0,
    currentLetterId: 0,
    setCurrentAuditorId: () => { },
    setCurrentLetterCpfId: () => { },
    setCurrentLetterId: () => { },
});

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(({ theme }) => ({
    width: 180,
    height: 37,
    marginRight: 5,
    marginLeft: 7,
    paddingLeft: 10,
    paddingRight: 5,
    '& input': {
        background: 'transparent !important',
        paddingLeft: '4px !important',
    }
}));


function ArcherApp() {

    //set up browser title
    const pageTitle = 'Extranet - Archer 3';
    document.title = pageTitle;

    useEffect(() => {
        getUserEmployeeId();
    }, [])

    const getUserEmployeeId = () => {
        let url = `/Archer3/GetUserEmployeeId`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetUserEmployeeId',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let auditorId = result.result;
                    setCurrentAuditorId(auditorId);
                    parseUrl();
                }
            })
    }

    const parseUrl = () => {
        const queryParams = new URLSearchParams(window.location.search)
        const cpfId = queryParams.get("cpfId");
        const letterId = queryParams.get("letterId");
        const searchInput = queryParams.get("search");
        if (letterId && cpfId) {
            setCurrentLetterCpfId(parseInt(cpfId));
            setCurrentLetterId(parseInt(letterId));
            toggleDisplayStatus('filers');
        }
        if (searchInput) {
            setCurrentSearchInput(searchInput);
            toggleDisplayStatus('filers');;
        }
    }

    //initial state set up
    const theme = useTheme();
    const [displayStatus, setDisplayStatus] = useState(DisplayStatus);
    const [componentName, setComponentName] = useState('Archer Dashboard');
    const [currentAuditorId, setCurrentAuditorId] = useState(defaultAuditorId);
    const [currentLetterCpfId, setCurrentLetterCpfId] = useState(0);
    const [currentLetterId, setCurrentLetterId] = useState(0);
    const [currentSearchInput, setCurrentSearchInput] = useState('');

    const Main = styled('main', { shouldForwardProp })(({ theme}) => ({
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create(
            'margin'
        ),
        [theme.breakpoints.up('md')]: {
            marginLeft: '20px',
            width: '100%'
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: '100%',
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: '100%',
            padding: '16px',
            marginRight: '10px'
        }
    }));

    const searchInputRef = useRef<HTMLInputElement>(null);

    const updateSearchPhrase = (event: any) => {
        setCurrentSearchInput(event.target.value);
        toggleDisplayStatus('filers');
        if (searchInputRef.current) {
            searchInputRef.current.value = "";
        }
    }

    const toggleDisplayStatus = (type: string)=> {
        let newStatus = { ...DisplayStatus };
        newStatus.Dashboard = 'hidden';
        newStatus.DashboardButton = 'active';

        switch (type) {
            case 'filers':
                newStatus.Filers = 'active';
                newStatus.FilersButton = 'hidden';
                newStatus.FilerSummary = 'active';
                newStatus.Referral = 'hidden';
                newStatus.ReferralButton = 'active';
                newStatus.Audit205 = 'hidden';
                newStatus.Audit205Button = 'active';
                setComponentName('Archer: Filers');
                break;
            case 'dashboard':
                newStatus.Dashboard = 'active';
                newStatus.DashboardButton = 'hidden';
                const queryParams = new URLSearchParams(window.location.search)
                const letterId = queryParams.get("letterId");
                const searchInput = queryParams.get("search");
                if (letterId || searchInput) {
                    //clear the url
                    window.location.href = '/apps/archer';
                }
                else {
                    setComponentName('Archer Dashboard');
                    setCurrentLetterCpfId(0);
                    setCurrentLetterId(0);
                    setCurrentSearchInput('');
                }

                break;
            case 'referrals':
                newStatus.Dashboard = 'hidden';
                newStatus.DashboardButton = 'active';
                newStatus.Filers = 'active';
                newStatus.FilersButton = 'active';
                newStatus.FilerSummary = 'hidden';
                newStatus.Referral = 'active';
                newStatus.ReferralButton = 'hidden';
                newStatus.Audit205 = 'hidden';
                newStatus.Audit205Button = 'active';
                setComponentName('Archer: Referrals');
                break;
            case 'audit205s':
                newStatus.Dashboard = 'hidden';
                newStatus.DashboardButton = 'active';
                newStatus.Filers = 'active';
                newStatus.FilersButton = 'active';
                newStatus.FilerSummary = 'hidden';
                newStatus.Referral = 'hidden';
                newStatus.ReferralButton = 'active';
                newStatus.Audit205Button = 'hidden';
                newStatus.Audit205 = 'active';
                setComponentName('Archer: Follow Up Scheduler');
                break;
        }
        setDisplayStatus(newStatus);
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <div>
            <AuditorCommunicationContext.Provider value={{
                currentAuditorId: currentAuditorId,
                currentLetterCpfId: currentLetterCpfId,
                currentLetterId: currentLetterId,
                setCurrentAuditorId: (newId: number) => {
                    setCurrentAuditorId(newId);
                    setDisplayStatus(DisplayStatus);
                    setComponentName('Archer Dashboard');
                },
                setCurrentLetterCpfId: (newId: number) => {
                    setCurrentLetterCpfId(newId);
                    toggleDisplayStatus('filers');
                },
                setCurrentLetterId: (newId: number) => {
                    setCurrentLetterId(newId);
                }
            }}>
            <Grid container sx={{ display: 'flex' }}>
                <CssBaseline />
                <Grid item xs={12}>
                    {/* top navigation bar */}
                    <AppBar
                        enableColorOnDark
                        position="fixed"
                        color="inherit"
                        elevation={0}
                        sx={{
                            bgcolor: '',
                            transition: 'none'
                        }}
                        className="top-navbar"
                    >
                        <Toolbar>
                            <Box
                                sx={{
                                    width: 228,
                                    display: 'flex',
                                }}
                            >
                                <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                                    <ButtonBase>
                                        <ExtranetLogo></ExtranetLogo>
                                    </ButtonBase>
                                </Box>
                            </Box>

                            <Box sx={{ flexGrow: 2 }} style={{ marginTop: 10 }}>
                                <Typography variant="h6" sx={{ color: theme.palette.info.main, fontWeight: 'bold' }}>
                                    {componentName}
                                </Typography>
                            </Box>
                            <Box sx={{ flexGrow: 1 }}></Box>
                            <Box sx={{ flexGrow: 1 }}>
                                <Stack spacing={2} direction="row">
                                     <span className={`${displayStatus.FilersButton}`}>
                                            <OutlineInputStyle
                                                placeholder='Search Filers...'
                                                inputRef={searchInputRef}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter")
                                                        updateSearchPhrase(e);
                                                }}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <FontAwesomeIcon icon={["fas", "search"]} />
                                                    </InputAdornment>
                                                }
                                                sx={{ fontSize: 16 }}
                                            />
                                    </span>
                                    <Button variant="outlined" className={`${displayStatus.DashboardButton}`} onClick={() => toggleDisplayStatus('dashboard')}>
                                        <span className="icon">
                                            <FontAwesomeIcon icon={["fas", "grip-horizontal"]} />
                                        </span>
                                        <span>Dashboard</span>
                                    </Button>
                                    <Button variant="outlined" className={`${displayStatus.FilersButton}`} onClick={() => toggleDisplayStatus('filers')}>
                                        <span className="icon">
                                            <FontAwesomeIcon icon={["fas", "users"]} />
                                        </span>
                                        <span>Filers</span>
                                    </Button>
                                    <Button variant="outlined" className={`${displayStatus.Audit205Button}`} onClick={() => toggleDisplayStatus('audit205s')}>
                                        <span className="icon">
                                            <FontAwesomeIcon icon={["fas", "calendar-days"]} />
                                        </span>
                                        <span>Follow Up Scheduler</span>
                                    </Button>
                                    <Button variant="outlined" className={`${displayStatus.ReferralButton}`} onClick={() => toggleDisplayStatus('referrals')}>
                                        <span className="icon">
                                            <FontAwesomeIcon icon={["fas", "gavel"]} />
                                        </span>
                                        <span>Referrals</span>
                                        </Button>
                                    <Button variant="outlined" href="/Archer3/Guide" target="_blank">
                                        <span className="icon">
                                            <FontAwesomeIcon icon={["fas", "book"]} />
                                        </span>
                                        <span>Manual</span>
                                    </Button>
                                </Stack>
                            </Box>
                        </Toolbar>
                    </AppBar>
                </Grid>
                <Grid item xs={12}>
                        {/* main content */}
                        <Main theme={theme}>
                            <div className="main-content">
                                <Dashboard displayStatus={displayStatus.Dashboard} toggleDisplayStatus={toggleDisplayStatus}></Dashboard>
                                <FilersRoot searchInput={currentSearchInput} displayStatus={displayStatus.Filers} filerSummaryDisplayStatus={displayStatus.FilerSummary} referralDisplayStatus={displayStatus.Referral} audit205DisplayStatus={displayStatus.Audit205}></FilersRoot>
                            </div>
                        </Main>
                    </Grid>
                </Grid>
            </AuditorCommunicationContext.Provider>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </div>
        );
}

export default ArcherApp;